'use client';

import React from 'react';
import SharedLayout from '../components/SharedLayout';
import Link from 'next/link';
import Image from 'next/image';
import { MotionDiv } from '@/components/motion';
import { Package, Headphones, CheckCircle, ArrowRight, DollarSign, TrendingUp, Droplet, Car, Sparkles, Users, ShoppingBag, Mail, Phone } from 'lucide-react';

export default function RevendedoresPage() {
  const productLines = [
    {
      icon: Sparkles,
      title: 'Productos de Limpieza',
      description: 'Para el hogar e industria',
      items: [
        'Detergentes biodegradables',
        'Desengrasantes multiuso',
        'Gel desinfectante',
        'Lavandina en gel',
        'Jabón líquido',
        'Suavizante de ropa'
      ]
    },
    {
      icon: Car,
      title: 'Cuidado Automotriz',
      description: 'Productos especializados',
      items: [
        'Detergente sintético',
        'Revividor de gomas y plásticos',
        'Aceite siliconado',
        'Anticongelante anticorrosivo'
      ]
    },
    {
      icon: Droplet,
      title: 'Mantenimiento de Piscinas',
      description: 'Línea completa',
      items: [
        'Cloro líquido y granulado',
        'Alguicidas',
        'Bactericidas',
        'Fungicidas'
      ]
    }
  ];

  const kitIncludes = [
    'Guía de fraccionamiento detallada',
    'Pack inicial de productos',
    'Productos de limpieza concentrados',
    'Orientación para venta minorista',
    'Asesoramiento técnico',
    'Listas de precios actualizadas'
  ];

  const benefits = [
    {
      icon: DollarSign,
      title: 'Precios Competitivos',
      description: 'Accede a precios mayoristas especiales y márgenes atractivos para tu negocio',
    },
    {
      icon: TrendingUp,
      title: 'Productos de Calidad',
      description: 'Evaluaciones permanentes para mantener los más altos estándares del mercado',
    },
    {
      icon: Users,
      title: 'Soporte Integral',
      description: 'Asesoramiento técnico y comercial constante para el crecimiento de tu negocio',
    },
    {
      icon: CheckCircle,
      title: 'Sustentabilidad',
      description: 'Productos con enfoque en sostenibilidad y compromiso con la calidad',
    },
  ];

  return (
    <SharedLayout>
      {/* Hero Section with Image */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-primary to-blue-700"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <MotionDiv
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-block mb-4">
                <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full inline-block">
                  <p className="text-sm font-medium text-white whitespace-nowrap">Programa de Distribuidores</p>
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Conviértete en Revendedor Droquimar
              </h1>
              <p className="text-xl text-white/90 mb-8">
                Únete a nuestra red de distribuidores y crece tu negocio con productos químicos y de limpieza de la más alta calidad
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  href="/revendedores/autogestion"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-brand-primary rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
                >
                  Acceder al Panel
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  href="/contacto"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-lg font-semibold hover:bg-white/20 transition-colors"
                >
                  Solicitar Información
                </Link>
              </div>
            </MotionDiv>

            <MotionDiv
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative h-[400px] rounded-2xl overflow-hidden shadow-2xl"
            >
              <Image
                src="/images/institution/Programa-revendedores.webp"
                alt="Programa de Revendedores Droquimar"
                fill
                className="object-cover"
                priority
              />
            </MotionDiv>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">

        {/* Benefits Section */}
        <section className="mb-20">
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">¿Por qué ser Revendedor Droquimar?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Beneficios exclusivos para nuestros distribuidores autorizados
            </p>
          </MotionDiv>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <MotionDiv
                  key={benefit.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
                  className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100"
                >
                  <div className="bg-red-50 rounded-xl w-14 h-14 flex items-center justify-center mb-4">
                    <Icon className="h-7 w-7 text-brand-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </MotionDiv>
              );
            })}
          </div>
        </section>

        {/* Product Lines Section */}
        <section className="mb-20">
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Líneas de Productos</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Amplio catálogo de productos químicos y de limpieza para diferentes sectores
            </p>
          </MotionDiv>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {productLines.map((line, index) => {
              const Icon = line.icon;
              return (
                <MotionDiv
                  key={line.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                  className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all border border-gray-100"
                >
                  <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl w-14 h-14 flex items-center justify-center mb-4">
                    <Icon className="h-7 w-7 text-brand-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {line.title}
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">{line.description}</p>
                  <ul className="space-y-3">
                    {line.items.map((item) => (
                      <li key={item} className="flex items-start text-sm text-gray-700">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </MotionDiv>
              );
            })}
          </div>
        </section>

        {/* Kit Emprendedor Section */}
        <section className="mb-20">
          <div className="bg-gradient-to-br from-red-50 to-blue-50 rounded-2xl shadow-xl p-8 md:p-12 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary/5 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/5 rounded-full -ml-24 -mb-24"></div>

            <div className="relative z-10">
              <MotionDiv
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-10"
              >
                <div className="bg-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <ShoppingBag className="h-10 w-10 text-brand-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Kit de Emprendedor</h2>
                <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                  Todo lo que necesitas para comenzar tu negocio de reventa con el pie derecho
                </p>
              </MotionDiv>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto mb-10">
                {kitIncludes.map((item, index) => (
                  <MotionDiv
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 + index * 0.05 }}
                    className="flex items-start bg-white rounded-lg p-4 shadow-sm"
                  >
                    <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{item}</span>
                  </MotionDiv>
                ))}
              </div>

              <div className="text-center">
                <p className="text-gray-700 mb-6 text-lg">
                  Portal de autogestión con instrucciones de fraccionamiento y soporte continuo
                </p>
                <Link
                  href="/revendedores/autogestion"
                  className="inline-flex items-center px-8 py-4 bg-brand-primary text-white rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-lg hover:shadow-xl"
                >
                  Acceder al Panel de Autogestión
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA Section */}
        <section>
          <div className="bg-gradient-to-br from-brand-primary to-red-900 rounded-2xl shadow-2xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-8 md:p-12 text-white">
                <MotionDiv
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    ¿Listo para Empezar?
                  </h2>
                  <p className="text-xl text-white/90 mb-8">
                    Contáctanos para obtener más información sobre cómo convertirte en revendedor autorizado de Droquimar
                  </p>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-center">
                      <div className="bg-white/20 rounded-lg p-3 mr-4">
                        <Package className="h-6 w-6" />
                      </div>
                      <span className="text-lg">2,400 m² de depósito en La Tablada</span>
                    </div>
                    <div className="flex items-center">
                      <div className="bg-white/20 rounded-lg p-3 mr-4">
                        <TrendingUp className="h-6 w-6" />
                      </div>
                      <span className="text-lg">Más de 40 años en el mercado</span>
                    </div>
                    <div className="flex items-center">
                      <div className="bg-white/20 rounded-lg p-3 mr-4">
                        <Headphones className="h-6 w-6" />
                      </div>
                      <span className="text-lg">Soporte técnico permanente</span>
                    </div>
                  </div>

                  <Link
                    href="/contacto"
                    className="inline-flex items-center px-8 py-4 bg-white text-brand-primary rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
                  >
                    Solicitar Información
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </MotionDiv>
              </div>

              <div className="bg-white p-8 md:p-12">
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Información de Contacto</h3>
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center mb-2">
                      <Phone className="h-5 w-5 text-brand-primary mr-2" />
                      <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Teléfono</p>
                    </div>
                    <a href="tel:+541146553067" className="text-lg text-brand-primary hover:underline font-medium">
                      +54 011 4655-3067
                    </a>
                  </div>
                  <div>
                    <div className="flex items-center mb-2">
                      <Phone className="h-5 w-5 text-brand-primary mr-2" />
                      <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">WhatsApp</p>
                    </div>
                    <a
                      href="https://api.whatsapp.com/send?phone=541161212156"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg text-brand-primary hover:underline font-medium"
                    >
                      +54 9 11 6121-2156
                    </a>
                  </div>
                  <div>
                    <div className="flex items-center mb-2">
                      <Mail className="h-5 w-5 text-brand-primary mr-2" />
                      <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Email</p>
                    </div>
                    <a href="mailto:ventas@droquimar.com" className="text-lg text-brand-primary hover:underline font-medium">
                      ventas@droquimar.com
                    </a>
                  </div>
                  <div>
                    <div className="flex items-center mb-2">
                      <Package className="h-5 w-5 text-brand-primary mr-2" />
                      <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Dirección</p>
                    </div>
                    <p className="text-lg text-gray-700">
                      Av. Crovara 2000/2014<br />
                      La Tablada, Buenos Aires
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </SharedLayout>
  );
}
